import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Info, CheckCircle2, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCart } from "@/context/CartContext";
import { getBrand, formatPrice, displayPrice } from "@/data/catalog";
import { fetchSettings, createOrder, createCheckout, getDeliveryQuote } from "@/lib/api";

function Field({ id, label, type = "text", value, onChange, placeholder, required }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm text-slate-700">{label} {required && <span className="text-red-600">*</span>}</Label>
      <Input id={id} type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} className="mt-1.5 rounded-sm border-slate-300" data-testid={`checkout-${id}`} />
    </div>
  );
}

export default function Checkout() {
  const { items, subtotal, hasRequestPrice, clear, count } = useCart();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", address: "", city: "", state: "", zip: "", notes: "" });
  const [fulfillment, setFulfillment] = useState("delivery");
  const [distance, setDistance] = useState("");
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(null);

  const isQuote = hasRequestPrice;

  useEffect(() => { fetchSettings().then(setSettings).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!isQuote && fulfillment === "delivery" && distance !== "") {
      getDeliveryQuote(subtotal, Number(distance)).then(setDeliveryInfo).catch(() => setDeliveryInfo(null));
    } else {
      setDeliveryInfo(null);
    }
  }, [distance, fulfillment, subtotal, isQuote]);

  const manualMethods = settings?.payments?.manual_methods || [];
  const onlineEnabled = settings?.payments?.online_enabled && !isQuote;

  useEffect(() => {
    if (isQuote) return;
    if (onlineEnabled) setPaymentMethod("online");
    else if (manualMethods.length) setPaymentMethod(`manual:${manualMethods[0]}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, isQuote]);

  const deliveryFee = fulfillment === "delivery" ? deliveryInfo?.fee ?? null : 0;
  const total = useMemo(() => (isQuote ? null : subtotal + (deliveryFee || 0)), [isQuote, subtotal, deliveryFee]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const method = isQuote ? "quote" : paymentMethod.startsWith("manual") ? "manual" : "online";
      const order = await createOrder({
        customer: { firstName: form.firstName, lastName: form.lastName, email: form.email, phone: form.phone },
        billing: { address: form.address, city: form.city, state: form.state, zip: form.zip },
        items: items.map((i) => ({ id: i.id, qty: i.qty })),
        fulfillment,
        distanceMiles: distance !== "" ? Number(distance) : null,
        paymentMethod: method,
        notes: form.notes + (paymentMethod.startsWith("manual") ? ` [Preferred payment: ${paymentMethod.split(":")[1]}]` : ""),
      });

      if (method === "online") {
        const { checkout_url } = await createCheckout(order.id, window.location.origin);
        window.location.href = checkout_url;
        return;
      }
      clear();
      setPlaced({ order, method });
    } catch (err) {
      toast.error("Something went wrong placing your order. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (placed) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-6 lg:px-8" data-testid="order-confirmation">
        <Seo title="Order received | CA HVAC Direct" description="Your order request has been received." />
        <CheckCircle2 className="mx-auto h-14 w-14 text-sky-600" />
        <h1 className="mt-5 font-heading text-3xl font-black tracking-tight text-slate-900">{placed.order.isQuote ? "Quote Request Received" : "Order Received"}</h1>
        <p className="mt-3 text-slate-600">
          Thank you! Your {placed.order.isQuote ? "quote request" : "order"} <span className="font-mono font-semibold">{placed.order.orderNumber}</span> has been received.
          {placed.order.isQuote ? " We'll be in touch with pricing and next steps." : " We'll confirm details and arrange payment/fulfillment."}
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Button asChild className="rounded-sm bg-sky-700 hover:bg-sky-800"><Link to="/shop">Continue Shopping</Link></Button>
          <Button asChild variant="outline" className="rounded-sm border-slate-300"><Link to="/contact">Contact Us</Link></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Seo title="Checkout | CA HVAC Direct" description="Checkout for CA HVAC Direct equipment orders." />
      <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">{isQuote ? "Request a Quote" : "Checkout"}</h1>

      {isQuote && (
        <div className="mt-6 flex items-start gap-3 rounded-sm border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900" data-testid="checkout-quote-notice">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
          <p>Your cart contains <strong>Request Price</strong> items. Submit your details and we'll respond with pricing — nothing is charged now.</p>
        </div>
      )}

      {items.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
          <p className="font-heading text-lg font-bold text-slate-900">Your cart is empty</p>
          <Button asChild className="mt-5 rounded-sm bg-sky-700 hover:bg-sky-800"><Link to="/shop">Browse Equipment</Link></Button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 grid gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            <section className="rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Customer Information</h2>
  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <Field id="firstName" label="First name" value={form.firstName} onChange={set("firstName")} placeholder="First name" required />
                <Field id="lastName" label="Last name" value={form.lastName} onChange={set("lastName")} placeholder="Last name" required />
                <Field id="email" label="Email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required />
                <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" required />
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Delivery or Pickup</h2>
              <RadioGroup value={fulfillment} onValueChange={setFulfillment} className="mt-5 grid gap-3 sm:grid-cols-2">
                {[{ id: "delivery", label: "Local Delivery", desc: "Deliver to my address" }, { id: "pickup", label: "Pickup", desc: "Pickup in West Covina, CA — details provided after confirmation" }].map((o) => (
                  <label key={o.id} htmlFor={`fulfill-${o.id}`} className="flex cursor-pointer items-start gap-3 rounded-sm border border-slate-200 p-4 has-[:checked]:border-sky-700 has-[:checked]:bg-sky-50">
                    <RadioGroupItem id={`fulfill-${o.id}`} value={o.id} className="mt-0.5" data-testid={`fulfillment-${o.id}`} />
                    <span><span className="block font-semibold text-slate-900">{o.label}</span><span className="block text-xs text-slate-500">{o.desc}</span></span>
                  </label>
                ))}
              </RadioGroup>
{fulfillment === "delivery" && (
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><Field id="address" label="Street address" value={form.address} onChange={set("address")} placeholder="Street address" /></div>
                <Field id="city" label="City" value={form.city} onChange={set("city")} placeholder="City" />
                <Field id="state" label="State" value={form.state} onChange={set("state")} placeholder="State" />
                <Field id="zip" label="ZIP code" value={form.zip} onChange={set("zip")} placeholder="ZIP" />
                {fulfillment === "delivery" && !isQuote && (
                  <div>
                    <Label htmlFor="distance" className="text-sm text-slate-700">Approx. distance (miles)</Label>
                    <Input id="distance" type="number" min="0" value={distance} onChange={(e) => setDistance(e.target.value)} placeholder="e.g. 25" className="mt-1.5 rounded-sm border-slate-300" data-testid="checkout-distance" />
                    <p className="mt-1 text-xs text-slate-400">Final delivery availability & fee confirmed by us.</p>
                  </div>
                )}
                </div>
)}
</section>

            {!isQuote && (
              <section className="rounded-lg border border-slate-200 bg-white p-6">
                <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Payment Method</h2>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="mt-5 space-y-3">
                  {onlineEnabled && (
                    <label htmlFor="pay-online" className="flex cursor-pointer items-start gap-3 rounded-sm border border-slate-200 p-4 has-[:checked]:border-sky-700 has-[:checked]:bg-sky-50">
                      <RadioGroupItem id="pay-online" value="online" className="mt-0.5" data-testid="payment-online" />
                      <span><span className="block font-semibold text-slate-900">Pay securely online (card)</span><span className="block text-xs text-slate-500">Visa, Mastercard, Amex via Stripe. Apple Pay / Google Pay where supported.</span></span>
                    </label>
                  )}
                  {manualMethods.map((m) => (
                    <label key={m} htmlFor={`pay-${m}`} className="flex cursor-pointer items-start gap-3 rounded-sm border border-slate-200 p-4 has-[:checked]:border-sky-700 has-[:checked]:bg-sky-50">
                      <RadioGroupItem id={`pay-${m}`} value={`manual:${m}`} className="mt-0.5" data-testid={`payment-manual-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                      <span><span className="block font-semibold text-slate-900">{m}</span><span className="block text-xs text-slate-500">We'll arrange this payment with you directly.</span></span>
                    </label>
                  ))}
                </RadioGroup>
              </section>
            )}

            <section className="rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Order Notes</h2>
              <Textarea value={form.notes} onChange={set("notes")} placeholder="Anything we should know? (optional)" className="mt-4 rounded-sm border-slate-300" data-testid="checkout-notes" />
            </section>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-28 rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Order Summary</h2>
              <Separator className="my-4" />
              <div className="max-h-48 space-y-3 overflow-y-auto">
                {items.map((item) => {
                  const dp = displayPrice(item);
                  return (
                    <div key={item.id} className="flex justify-between gap-3 text-sm">
                      <span className="min-w-0"><span className="block truncate text-slate-700">{item.name}</span><span className="font-mono text-xs text-slate-400">{getBrand(item.brandSlug)?.name} · Qty {item.qty}</span></span>
                      <span className="shrink-0 font-medium text-slate-900">{dp.mode === "show" ? formatPrice(dp.price * item.qty) : "Req."}</span>
                    </div>
                  );
                })}
              </div>
              <Separator className="my-4" />
              {isQuote ? (
                <p className="text-sm text-amber-600">Pricing will be confirmed in your quote.</p>
              ) : (
                <>
                  <div className="flex justify-between text-sm text-slate-600"><span>Subtotal</span><span className="font-semibold text-slate-900">{formatPrice(subtotal)}</span></div>
                  <div className="mt-2 flex justify-between text-sm text-slate-600">
                    <span>Delivery</span>
                    <span className="font-medium text-slate-900">{fulfillment !== "delivery" ? "—" : deliveryInfo ? (deliveryInfo.fee === null ? "Not available" : deliveryInfo.fee === 0 ? "Free" : formatPrice(deliveryInfo.fee)) : "Confirmed after order"}</span>
                  </div>
                  <Separator className="my-3" />
                  <div className="flex justify-between font-heading text-base font-bold text-slate-900"><span>Total</span><span>{total != null ? formatPrice(total) : "—"}</span></div>
                </>
              )}

              <Button type="submit" disabled={submitting} className="mt-6 w-full rounded-sm bg-sky-700 hover:bg-sky-800" data-testid="place-order-button">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : isQuote ? "Submit Quote Request" : paymentMethod === "online" ? <><Lock className="h-4 w-4" /> Pay Securely</> : "Place Order"}
              </Button>
              <p className="mt-3 text-center text-xs text-slate-400">{isQuote ? "No payment is taken for quote requests." : paymentMethod === "online" ? "You'll be redirected to secure payment." : "We'll contact you to arrange payment."}</p>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
